import { ChakraProvider } from "@chakra-ui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { theme } from "../theme";
import ProfileContextProvider from "../contexts/profile";
import { CourseImageProvider as CourseImageContextProvider } from "../contexts/course-image";
import ProductContextProvider from "../contexts/product";
import { Suspense, useState } from "react";
import ProtectedRoute from "./ProtectedRoute";
import ErrorBoundary from "./ErrorBoundary";
import { CustomSnackbarContainer } from "mainstack-design-system";
import reactLazyWithRetry from "@fatso83/retry-dynamic-import/react-lazy";
import "../styles/global.css";
import Loader from "./Loader";
import ProtectedAuthRoutes from "./ProtectedAuthRoutes";
import NavigateWithParams from "./NavigateWithParams";

const CardDetails = reactLazyWithRetry(() => import("@/pages/CardDetails"));
const CoursePreviewPage = reactLazyWithRetry(
  () => import("@/pages/CoursePreviewPage")
);
const Verify = reactLazyWithRetry(() => import("@/pages/Verify"));
const Login = reactLazyWithRetry(() => import("@/pages/Login"));
const Dashboard = reactLazyWithRetry(() => import("@/pages/Dashboard"));
const PostHogTest = reactLazyWithRetry(() => import("@/pages/PostHogTest"));
const SentryTest = reactLazyWithRetry(() => import("@/pages/SentryTest"));
const NotFound = reactLazyWithRetry(() => import("@/pages/NotFound"));
const PaymentRedirect = reactLazyWithRetry(
  () => import("@/pages/PaymentRedirect")
);
const StartCourse = reactLazyWithRetry(() => import("@/pages/StartCoursePage"));
const CourseOverviewPage = reactLazyWithRetry(
  () => import("@/pages/CourseOverviewPage")
);

function App() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes in milliseconds
          },
        },
      })
  );

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ChakraProvider theme={theme}>
          <BrowserRouter>
            <ProfileContextProvider>
              <CourseImageContextProvider>
                <ProductContextProvider>
                  <CustomSnackbarContainer />
                  <Suspense fallback={<Loader />}>
                    <Routes>
                      <Route
                        path="/auth"
                        element={
                          <ProtectedAuthRoutes>
                            <Login />
                          </ProtectedAuthRoutes>
                        }
                      />
                      <Route
                        path="/auth/verify"
                        element={
                          <ProtectedAuthRoutes>
                            <Verify />
                          </ProtectedAuthRoutes>
                        }
                      />
                      <Route
                        path="/payment-redirect"
                        element={<PaymentRedirect />}
                      />
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <Dashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/course/:id"
                        element={
                          <ProtectedRoute>
                            <CourseOverviewPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/course/:id/preview"
                        element={<CoursePreviewPage />}
                      />

                      <Route
                        path="/course/:id/start"
                        element={
                          <ProtectedRoute>
                            <StartCourse />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/card-details/:id"
                        element={
                          <ProtectedRoute>
                            <CardDetails />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/sentry-test" element={<SentryTest />} />
                      <Route path="/posthog-test" element={<PostHogTest />} />
                      <Route
                        path="/"
                        element={<NavigateWithParams to="/dashboard" replace />}
                      />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </ProductContextProvider>
              </CourseImageContextProvider>
            </ProfileContextProvider>
          </BrowserRouter>
        </ChakraProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
